import React, { useState, useEffect } from 'react';
import { TextField } from '@deque/cauldron-react';
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
  type ControllerRenderProps,
  type FormState
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import emailValidator from 'email-validator';

export interface Props<T extends FieldValues> {
  label: string;
  name: FieldPath<T>;
  id: string;
  disabled: boolean;
  formControl: Control<T>;
}

interface EmailTextFieldProps<T extends FieldValues> {
  field: ControllerRenderProps<T, FieldPath<T>>;
  formState: FormState<T>;
  id: string;
  label: string;
  disabled: boolean;
  name: FieldPath<T>;
}

const EmailTextField = <T extends FieldValues>({
  field: { ref, value, onChange, ...field },
  formState,
  id,
  label,
  disabled,
  name
}: EmailTextFieldProps<T>) => {
  const [displayValue, setDisplayValue] = useState('');

  // Initialize display value from form value
  useEffect(() => {
    const normalizedValue = Array.isArray(value) ? value.join(', ') : '';
    setDisplayValue(normalizedValue);
  }, [value]);

  const handleChange = (inputValue: string) => {
    setDisplayValue(inputValue);

    // If there's an error and user is typing, trigger validation to potentially clear it
    if (formState.errors[name]) {
      const emails = inputValue
        .split(',')
        .map(email => email.trim())
        .filter(Boolean);

      onChange(emails);
    }
  };

  const handleBlur = () => {
    const emails = displayValue
      .split(',')
      .map(email => email.trim())
      .filter(Boolean);

    const uniqueEmails = Array.from(new Set(emails));

    onChange(uniqueEmails);
    setDisplayValue(uniqueEmails.join(', '));
  };

  return (
    <TextField
      {...field}
      fieldRef={ref}
      id={id}
      label={label}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      multiline
      disabled={disabled}
      error={formState.errors[name]?.message}
    />
  );
};

const EmailsSetting = <T extends FieldValues>({
  formControl,
  label,
  name,
  id,
  disabled
}: Props<T>) => {
  const { t } = useTranslation();

  const validateEmails = (emails: string[]) => {
    if (emails.length === 0) {
      return undefined; // Allow empty
    }

    const invalidEmails = emails.filter(
      (email: string) => !emailValidator.validate(email)
    );

    if (!invalidEmails.length) {
      return true;
    }

    return invalidEmails.length === 1
      ? t(`The following email address is invalid: {{ email }}`, {
          email: invalidEmails[0]
        })
      : t(
          `The following ({{ count }}) email addresses are invalid: {{ emails }}`,
          {
            count: invalidEmails.length,
            emails: invalidEmails.join(', ')
          }
        );
  };

  return (
    <Controller
      control={formControl}
      name={name}
      rules={{
        validate: validateEmails
      }}
      render={({ field, formState }) => (
        <EmailTextField
          field={field}
          formState={formState}
          id={id}
          label={label}
          disabled={disabled}
          name={name}
        />
      )}
    />
  );
};

export default EmailsSetting;
